/**
 * Google Analytics Event Tracking Helper
 * Safely sends events to Google Analytics (gtag)
 */

// Declare gtag for TypeScript
declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
    dataLayer?: any[];
  }
}

/**
 * Send custom event to Google Analytics
 * @param eventName - Name of the event (e.g., 'register_attempt')
 * @param eventParams - Additional parameters to send with the event
 */
export const trackEvent = (eventName: string, eventParams: Record<string, any> = {}) => {
  try {
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', eventName, eventParams);
      console.log(`[GA Event] ${eventName}`, eventParams);
    } else {
      console.warn('Google Analytics not loaded');
    }
  } catch (error) {
    console.error('Error tracking event:', error);
  }
};

/**
 * Set user properties in Google Analytics
 * @param userId - Unique user identifier
 * @param userProperties - Additional user properties
 */
export const setUserProperties = (userId: string, userProperties: Record<string, any> = {}) => {
  try {
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('set', 'user_properties', {
        user_id: userId,
        ...userProperties
      });
      console.log(`[GA User] Set user properties for ${userId}`, userProperties);
    }
  } catch (error) {
    console.error('Error setting user properties:', error);
  }
};

/**
 * Set user ID for Google Analytics
 * @param userId - Unique user identifier
 */
export const setUserId = (userId: string) => {
  try {
    if (typeof window !== 'undefined' && window.gtag) {
      const gaId = process.env.REACT_APP_GA_MEASUREMENT_ID || 'G-VJ9QBGH9YT';
      window.gtag('config', gaId, {
        user_id: userId
      });
      console.log(`[GA User] Set user ID: ${userId}`);
    }
  } catch (error) {
    console.error('Error setting user ID:', error);
  }
};

// ============================================
// Pre-defined Event Tracking Functions
// ============================================

/**
 * Track registration attempt
 * GDPR/CCPA compliant - does not send PII
 */
export const trackRegisterAttempt = () => {
  trackEvent('register_attempt', {
    method: 'email'
  });
};

/**
 * Track successful registration
 * GDPR/CCPA compliant - does not send PII
 */
export const trackRegisterSuccess = () => {
  trackEvent('sign_up', {
    method: 'email'
  });
};

/**
 * Track registration error
 * GDPR/CCPA compliant - does not send PII
 */
export const trackRegisterError = (errorMessage: string) => {
  trackEvent('register_error', {
    error_message: errorMessage
  });
};

/**
 * Track login attempt
 * GDPR/CCPA compliant - does not send PII
 */
export const trackLoginAttempt = () => {
  trackEvent('login_attempt', {
    method: 'email'
  });
};

/**
 * Track successful login
 * GDPR/CCPA compliant - only sends user_id, no email or name
 */
export const trackLoginSuccess = (userId: string) => {
  // Set user ID for all future events
  setUserId(userId);

  // Track login event (no PII)
  trackEvent('login', {
    method: 'email',
    user_id: userId
  });
};

/**
 * Track login failure
 * GDPR/CCPA compliant - does not send PII
 */
export const trackLoginError = (errorMessage: string) => {
  trackEvent('login_error', {
    error_message: errorMessage
  });
};

/**
 * Track endpoint creation steps
 */
export const trackEndpointStep = (step: 1 | 2 | 3, endpointId?: string) => {
  trackEvent('endpoint_creation_step', {
    step: step,
    step_name: step === 1 ? 'basic_info' : step === 2 ? 'schema_definition' : 'testing',
    endpoint_id: endpointId || 'new',
  });
};

/**
 * Track endpoint submission (complete)
 */
export const trackEndpointSubmit = (endpointId: string, endpointName: string) => {
  trackEvent('endpoint_created', {
    endpoint_id: endpointId,
    endpoint_name: endpointName,
  });
};

/**
 * Track OAuth login (Google, Microsoft, GitHub)
 * GDPR/CCPA compliant - does not send PII
 */
export const trackOAuthLogin = (provider: 'google' | 'microsoft' | 'github') => {
  trackEvent('login', {
    method: provider
  });
};

/**
 * Track OAuth registration
 * GDPR/CCPA compliant - does not send PII
 */
export const trackOAuthRegister = (provider: 'google' | 'microsoft' | 'github') => {
  trackEvent('sign_up', {
    method: provider
  });
};
